export interface StaffSquadLocation {
  id?: string;
  name?: string;
  address?: string;
  state?: string;
  latitude?: number;
  longitude?: number;
}

export interface StaffSquadSchedule {
  mainDate?: string;
  startDate?: string;
  endDate?: string;
  dayOfWeek?: string;
  startTime?: string;
  endTime?: string;
  timezone?: string;
}

export interface StaffSquadListing {
  id: string;
  title: string;
  code?: string;
  locationId?: string;
  locationName?: string;
  schoolName?: string;
  curriculumId?: string;
  curriculumName?: string;
  seasonId?: string;
  seasonName?: string;
  term?: string;
  status?: string;
  staffingNeeded?: boolean;
  schedule?: StaffSquadSchedule;
  timezone?: string;
  location?: StaffSquadLocation;
}

export function displayCurriculum(listing: Partial<StaffSquadListing>): string {
  const explicit = String(listing.curriculumName || '').trim();
  const normalizedExplicit = explicit.toLowerCase().replace(/[^a-z0-9]/g, '');
  if (normalizedExplicit.startsWith('musicwinter')) return 'Music';
  if (explicit) return explicit;
  const id = String(listing.curriculumId || 'music').trim();
  const normalizedId = id.toLowerCase().replace(/[^a-z0-9]/g, '');
  if (normalizedId === 'music' || normalizedId.startsWith('musicwinter')) return 'Music';
  return id
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

export function listingNeedsStaffing(listing: Partial<StaffSquadListing>): boolean {
  // The public fallback intentionally omits teacher assignments. Until the
  // staff projection is available, never claim that staffing is assigned.
  return listing.staffingNeeded !== false;
}

export function displaySeason(listing: Partial<StaffSquadListing>): string {
  const explicit = String(listing.seasonName || listing.term || '').trim();
  if (explicit) return explicit;
  return String(listing.seasonId || 'Season not set')
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

export function formatListingDateRange(listing: Partial<StaffSquadListing>): string {
  const schedule = listing.schedule || {};
  const start = String(schedule.mainDate || schedule.startDate || '').trim();
  const end = String(schedule.mainDate || schedule.endDate || '').trim();
  if (!start) return 'Dates to be announced';

  const format = (ymd: string) => {
    const date = new Date(`${ymd}T12:00:00`);
    if (Number.isNaN(date.getTime())) return ymd;
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  };
  return end && end !== start ? `${format(start)} – ${format(end)}` : format(start);
}

export function formatListingTime(listing: Partial<StaffSquadListing>): string {
  const schedule = listing.schedule || {};
  const start = String(schedule.startTime || '').trim();
  const end = String(schedule.endTime || '').trim();
  if (!start && !end) return 'Time to be announced';
  const timezone = String(schedule.timezone || listing.timezone || '').trim();
  return `${start}${end ? ` – ${end}` : ''}${timezone ? ` (${timezone})` : ''}`;
}

export function coordinateForListing(
  listing: Partial<StaffSquadListing>,
  canonicalLocation?: StaffSquadLocation,
): { lat: number; lng: number } | null {
  const source = canonicalLocation || listing.location;
  const lat = Number(source?.latitude);
  const lng = Number(source?.longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng) || (lat === 0 && lng === 0)) return null;
  return { lat, lng };
}

export function listingFromSquad(
  squad: Partial<StaffSquadListing> & {
    id: string;
    leadTeacherId?: string | null;
    regularTeacher?: string | null;
    termId?: string;
    locationName?: string;
    schoolName?: string;
  },
  location?: StaffSquadLocation,
): StaffSquadListing {
  return {
    id: squad.id,
    title: String(squad.title || '').trim() || 'Squad',
    code: squad.code,
    locationId: squad.locationId,
    locationName: location?.name || squad.locationName,
    schoolName: location?.name || squad.schoolName,
    curriculumId: squad.curriculumId,
    curriculumName: squad.curriculumName,
    seasonId: squad.seasonId || squad.termId,
    seasonName: squad.seasonName,
    term: squad.term,
    status: squad.status,
    staffingNeeded: squad.staffingNeeded
      ?? !(String(squad.leadTeacherId || '').trim() || String(squad.regularTeacher || '').trim()),
    schedule: squad.schedule,
    timezone: squad.timezone,
    location: location
      ? {
        id: location.id,
        name: location.name,
        address: location.address,
        state: location.state,
        latitude: location.latitude,
        longitude: location.longitude,
      }
      : squad.location,
  };
}

export function mapClusterKey(
  listing: Partial<StaffSquadListing>,
  position: { lat: number; lng: number },
): string {
  const locationId = String(listing.locationId || '').trim();
  if (locationId) return `location:${locationId}`;
  return `point:${position.lat.toFixed(5)},${position.lng.toFixed(5)}`;
}

type ProgramSeason = 'winter' | 'spring' | 'summer' | 'fall';

function ymdFromUnknown(value: unknown): string | null {
  const text = String(value || '').trim();
  const match = text.match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : null;
}

function localYmd(date: Date): string {
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-');
}

function seasonFromMonth(month: number): ProgramSeason {
  if (month <= 3) return 'winter';
  if (month <= 6) return 'spring';
  if (month <= 9) return 'summer';
  return 'fall';
}

function termIdForDate(ymd: string): string | null {
  const match = ymd.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  if (!Number.isFinite(year) || !Number.isFinite(month)) return null;
  return `${seasonFromMonth(month)}${year}`;
}

function nextTermId(termId: string): string | null {
  const match = termId.match(/^(winter|spring|summer|fall)(\d{4})$/);
  if (!match) return null;
  const season = match[1] as ProgramSeason;
  const year = Number(match[2]);
  if (season === 'winter') return `spring${year}`;
  if (season === 'spring') return `summer${year}`;
  if (season === 'summer') return `fall${year}`;
  return `winter${year + 1}`;
}

function listingTermId(listing: Partial<StaffSquadListing>): string | null {
  const candidates = [
    listing.seasonId,
    listing.term,
    listing.seasonName,
  ].map((value) => String(value || '').trim().toLowerCase());
  for (const candidate of candidates) {
    const compact = candidate.replace(/[^a-z0-9]/g, '');
    if (/^(winter|spring|summer|fall)\d{4}$/.test(compact)) return compact;
  }
  return null;
}

/**
 * Discovery maps should only include programs that are in session now, or whose
 * remaining dates still fall in the current or next season. Past winter/spring
 * terms and far-future seasons stay hidden.
 */
export function isCurrentOrUpcomingListing(
  listing: Partial<StaffSquadListing>,
  today: Date = new Date(),
): boolean {
  const status = String(listing.status || '').trim().toLowerCase();
  if (['archived', 'canceled', 'cancelled'].includes(status)) return false;

  const schedule = listing.schedule || {};
  const start = ymdFromUnknown(schedule.mainDate || schedule.startDate);
  const end = ymdFromUnknown(schedule.mainDate || schedule.endDate || schedule.startDate);
  const todayYmd = localYmd(today);
  const currentTerm = termIdForDate(todayYmd);
  const upcomingTerm = currentTerm ? nextTermId(currentTerm) : null;
  const allowedTerms = new Set([currentTerm, upcomingTerm].filter(Boolean) as string[]);

  if (end && end < todayYmd) return false;
  if (start && start <= todayYmd && (!end || end >= todayYmd)) return true;
  if (start && start > todayYmd) {
    const startTerm = termIdForDate(start);
    return Boolean(startTerm && allowedTerms.has(startTerm));
  }

  const seasonTerm = listingTermId(listing);
  if (seasonTerm) return allowedTerms.has(seasonTerm);
  return false;
}
